import { useState, useCallback, useEffect, useRef } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { getOCELMetadata } from "../../../../services/api/ocel.api";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { useGlobalFilters } from "../../../../stores/GlobalFilterContext/GlobalFilterContext";

const formatDateISO = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return formatDateISO(d);
};

const normalizeSelectedDateForGranularity = (selectedDate, granularity) => {
  if (!selectedDate || granularity === "day") return selectedDate;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(selectedDate);
  if (!m) return selectedDate;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const pad2 = (n) => String(n).padStart(2, "0");

  if (granularity === "month") {
    return `${year}-${pad2(month)}-01`;
  }
  if (granularity === "year") {
    return `${year}-01-01`;
  }
  if (granularity === "week") {
    const dt = new Date(Date.UTC(year, month - 1, day));
    const mondayOffset = (dt.getUTCDay() + 6) % 7; // Mon=0...Sun=6
    dt.setUTCDate(dt.getUTCDate() - mondayOffset);
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
  }
  return selectedDate;
};

const useResourceUtilizationKPI = (activeFile, initialSettings = null) => {
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const { filterState } = useGlobalFilters();
  const configKey = `resourceUtilizationKPIConfig_${activeFile || "default"}`;
  const viewKey = `resourceUtilizationKPIView_${activeFile || "default"}`;

  // --- Data / loading state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [datasetLastDate, setDatasetLastDate] = useState(null);

  // --- Metadata state ---
  const [objectTypes, setObjectTypes] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // --- TileConfig (persistent, set in Edit Mode) ---
  const [title, setTitle] = useState("Resource Utilization");
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [cycleStartActivity, setCycleStartActivity] = useState("cycle_start");
  const [cycleEndActivity, setCycleEndActivity] = useState("cycle_end");
  const [configuredResources, setConfiguredResources] = useState([]);
  const [shiftStart, setShiftStart] = useState("06:00");
  const [shiftEnd, setShiftEnd] = useState("14:00");
  const [breaks, setBreaks] = useState([]);
  const [toleranceThreshold, setToleranceThreshold] = useState(60);
  const [ignoreShortTransitions, setIgnoreShortTransitions] = useState(false);
  const [includeNonWorkingDays, setIncludeNonWorkingDays] = useState(false);
  const [includeNoDataResources, setIncludeNoDataResources] = useState(false);
  const [multiResourceMode, setMultiResourceMode] = useState("accumulated");
  const [enabledGranularities, setEnabledGranularities] = useState([
    "day",
    "week",
    "month",
    "year",
  ]);
  const [resourceColumn, setResourceColumn] = useState("ressource");

  // --- ViewState (runtime, set in View Mode) ---
  const [viewResources, setViewResources] = useState([]);
  const [granularity, setGranularity] = useState("day");
  const [selectedDate, setSelectedDate] = useState(yesterdayISO());
  const [viewMode, setViewMode] = useState("granularity"); // "granularity" | "daterange"
  const [dateRangeStart, setDateRangeStart] = useState(yesterdayISO());
  const [dateRangeEnd, setDateRangeEnd] = useState(yesterdayISO());

  // --- Request dedup refs ---
  const inFlightSignatureRef = useRef(null);
  const lastResolvedSignatureRef = useRef(null);
  const lastResolvedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);

  // --- Restore TileConfig from localStorage ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(configKey);
      if (!raw) return;
      const cfg = JSON.parse(raw);

      if (typeof cfg.title === "string") setTitle(cfg.title);
      if (typeof cfg.selectedObjectType === "string")
        setSelectedObjectType(cfg.selectedObjectType);
      if (typeof cfg.cycleStartActivity === "string")
        setCycleStartActivity(cfg.cycleStartActivity);
      if (typeof cfg.cycleEndActivity === "string")
        setCycleEndActivity(cfg.cycleEndActivity);
      if (Array.isArray(cfg.configuredResources))
        setConfiguredResources(cfg.configuredResources);
      if (typeof cfg.shiftStart === "string") setShiftStart(cfg.shiftStart);
      if (typeof cfg.shiftEnd === "string") setShiftEnd(cfg.shiftEnd);
      if (Array.isArray(cfg.breaks)) setBreaks(cfg.breaks);
      if (typeof cfg.toleranceThreshold === "number")
        setToleranceThreshold(cfg.toleranceThreshold);
      if (typeof cfg.ignoreShortTransitions === "boolean")
        setIgnoreShortTransitions(cfg.ignoreShortTransitions);
      if (typeof cfg.includeNonWorkingDays === "boolean")
        setIncludeNonWorkingDays(cfg.includeNonWorkingDays);
      if (typeof cfg.includeNoDataResources === "boolean")
        setIncludeNoDataResources(cfg.includeNoDataResources);
      if (typeof cfg.multiResourceMode === "string")
        setMultiResourceMode(cfg.multiResourceMode);
      if (
        Array.isArray(cfg.enabledGranularities) &&
        cfg.enabledGranularities.length > 0
      )
        setEnabledGranularities(cfg.enabledGranularities);
    } catch {
      // ignore
    }
  }, [configKey]);

  // --- Restore ViewState from localStorage ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(viewKey);
      if (!raw) return;
      const vs = JSON.parse(raw);

      if (typeof vs.granularity === "string") setGranularity(vs.granularity);
      if (typeof vs.selectedDate === "string") setSelectedDate(vs.selectedDate);
      if (Array.isArray(vs.viewResources)) setViewResources(vs.viewResources);
      if (vs.viewMode === "granularity" || vs.viewMode === "daterange")
        setViewMode(vs.viewMode);
      if (typeof vs.dateRangeStart === "string")
        setDateRangeStart(vs.dateRangeStart);
      if (typeof vs.dateRangeEnd === "string") setDateRangeEnd(vs.dateRangeEnd);
    } catch {
      // ignore
    }
  }, [viewKey]);

  // Restore from centralised config (overrides localStorage on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.selectedObjectType !== undefined) setSelectedObjectType(s.selectedObjectType);
    if (s.cycleStartActivity !== undefined) setCycleStartActivity(s.cycleStartActivity);
    if (s.cycleEndActivity !== undefined) setCycleEndActivity(s.cycleEndActivity);
    if (s.configuredResources !== undefined) setConfiguredResources(s.configuredResources);
    if (s.shiftStart !== undefined) setShiftStart(s.shiftStart);
    if (s.shiftEnd !== undefined) setShiftEnd(s.shiftEnd);
    if (s.breaks !== undefined) setBreaks(s.breaks);
    if (s.toleranceThreshold !== undefined) setToleranceThreshold(s.toleranceThreshold);
    if (s.ignoreShortTransitions !== undefined) setIgnoreShortTransitions(s.ignoreShortTransitions);
    if (s.includeNonWorkingDays !== undefined) setIncludeNonWorkingDays(s.includeNonWorkingDays);
    if (s.includeNoDataResources !== undefined) setIncludeNoDataResources(s.includeNoDataResources);
    if (s.multiResourceMode !== undefined) setMultiResourceMode(s.multiResourceMode);
    if (s.enabledGranularities !== undefined) setEnabledGranularities(s.enabledGranularities);
    if (s.viewResources !== undefined) setViewResources(s.viewResources);
    if (s.granularity !== undefined) setGranularity(s.granularity);
    if (s.selectedDate !== undefined) setSelectedDate(s.selectedDate);
    if (s.viewMode !== undefined) setViewMode(s.viewMode);
    if (s.dateRangeStart !== undefined) setDateRangeStart(s.dateRangeStart);
    if (s.dateRangeEnd !== undefined) setDateRangeEnd(s.dateRangeEnd);
  }, []);

  // --- Persist TileConfig ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        configKey,
        JSON.stringify({
          title,
          selectedObjectType,
          cycleStartActivity,
          cycleEndActivity,
          configuredResources,
          shiftStart,
          shiftEnd,
          breaks,
          toleranceThreshold,
          ignoreShortTransitions,
          includeNonWorkingDays,
          includeNoDataResources,
          multiResourceMode,
          enabledGranularities,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    configKey,
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    enabledGranularities,
  ]);

  // --- Persist ViewState ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        viewKey,
        JSON.stringify({
          granularity,
          selectedDate,
          viewResources,
          viewMode,
          dateRangeStart,
          dateRangeEnd,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    viewKey,
    granularity,
    selectedDate,
    viewResources,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
  ]);

  // --- Break helpers ---
  const addBreak = useCallback(() => {
    setBreaks((prev) => [...prev, { start: "12:00", end: "12:30" }]);
  }, []);

  const removeBreak = useCallback((idx) => {
    setBreaks((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateBreak = useCallback((idx, field, value) => {
    setBreaks((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
    );
  }, []);

  // --- Reactive metadata sync from OcelContext (zero API calls when no filters) ---
  useEffect(() => {
    if (!metadata?.object_types) return;
    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );
    if (hasActiveFilters) return;

    const types = Object.keys(metadata.object_types);
    setObjectTypes(types);

    if (metadata.resource_values) {
      setAvailableResources(metadata.resource_values);
    }
    if (metadata.resource_column) {
      setResourceColumn(metadata.resource_column);
    }
    if (metadata.date_range?.end) {
      const lastDate = metadata.date_range.end.slice(0, 10);
      setDatasetLastDate(lastDate);
      setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
    }
    setIsMetadataLoaded(true);
  }, [metadata, filterState, activeFile]);

  // Auto-select first object type once types are loaded and nothing is selected
  useEffect(() => {
    if (!selectedObjectType && objectTypes.length > 0) {
      setSelectedObjectType(objectTypes[0]);
    }
  }, [objectTypes, selectedObjectType]);

  // --- Fetch metadata (called only when filters are active) ---
  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );

    if (!hasActiveFilters) {
      // Unfiltered data is already synced from OcelContext — nothing to do
      setIsMetadataLoaded(true);
      return true;
    }

    setLoading(true);
    setError(null);

    const params = { file_type: activeFile };

    try {
      const [objectTypesResponse, resourceResponse] = await Promise.all([
        api.get("/object-types", params, {}, "Object Types"),
        api.get("/ocel/resource-values", params, {}, "Resource Values"),
      ]);

      const types = objectTypesResponse.objectTypes || [];
      setObjectTypes(types);

      const resources = resourceResponse.values || [];
      setAvailableResources(resources);

      if (resourceResponse.used_column) {
        setResourceColumn(resourceResponse.used_column.replace(/^ocel:/, ""));
      }

      try {
        const metaRes = await getOCELMetadata(activeFile);
        if (metaRes?.date_range?.end) {
          const lastDate = metaRes.date_range.end.slice(0, 10);
          setDatasetLastDate(lastDate);
          setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
        }
      } catch { /* non-critical */ }

      setIsMetadataLoaded(true);
      return true;
    } catch (err) {
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, api, filterState]);

  // --- Fetch availability ---
  const fetchAvailability = useCallback(
    async (overrides = {}) => {
      const effectiveConfiguredResources =
        overrides.configuredResources ?? configuredResources;
      const effectiveViewResources = overrides.viewResources ?? viewResources;
      const effectiveResources =
        effectiveViewResources.length > 0
          ? effectiveViewResources
          : effectiveConfiguredResources;
      const effectiveSelectedObjectType =
        overrides.selectedObjectType ?? selectedObjectType;
      const effectiveSelectedDate = overrides.selectedDate ?? selectedDate;
      const effectiveShiftStart = overrides.shiftStart ?? shiftStart;
      const effectiveShiftEnd = overrides.shiftEnd ?? shiftEnd;
      const effectiveBreaks = overrides.breaks ?? breaks;
      const effectiveToleranceThreshold =
        overrides.toleranceThreshold ?? toleranceThreshold;
      const effectiveIgnoreShortTransitions =
        overrides.ignoreShortTransitions ?? ignoreShortTransitions;
      const effectiveIncludeNonWorkingDays =
        overrides.includeNonWorkingDays ?? includeNonWorkingDays;
      const effectiveIncludeNoDataResources =
        overrides.includeNoDataResources ?? includeNoDataResources;
      const effectiveMultiResourceMode =
        overrides.multiResourceMode ?? multiResourceMode;
      const effectiveGranularity = overrides.granularity ?? granularity;
      const effectiveEnabledGranularities =
        overrides.enabledGranularities ?? enabledGranularities;
      const normalizedGranularityDate = normalizeSelectedDateForGranularity(
        effectiveSelectedDate,
        effectiveGranularity,
      );
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart =
        overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (!activeFile) {
        setError("No active file selected");
        return false;
      }
      if (!effectiveSelectedObjectType) {
        setError("Please select an object type");
        return false;
      }
      if (effectiveResources.length === 0) {
        setError("Please select at least one resource");
        return false;
      }

      const isDateRange = effectiveViewMode === "daterange";
      if (!isDateRange && !effectiveSelectedDate) {
        setError("Please select a date");
        return false;
      }
      if (isDateRange && (!effectiveDateRangeStart || !effectiveDateRangeEnd)) {
        setError("Please select start and end dates");
        return false;
      }

      const toTimestamp = (hhmm) => `${hhmm}:00`;

      const payload = {
        file_type: activeFile,
        resources: effectiveResources,
        shift: {
          start_time: toTimestamp(effectiveShiftStart),
          end_time: toTimestamp(effectiveShiftEnd),
        },
        breaks: effectiveBreaks.map((b) => ({
          start_time: toTimestamp(b.start),
          end_time: toTimestamp(b.end),
        })),
        tolerance_threshold_seconds: effectiveToleranceThreshold,
        ignore_short_transitions: effectiveIgnoreShortTransitions,
        include_non_working_days: effectiveIncludeNonWorkingDays,
        include_no_data_resources: effectiveIncludeNoDataResources,
        multi_resource_mode: effectiveMultiResourceMode,
        cycle_start_column: cycleStartActivity,
        cycle_end_column: cycleEndActivity,
        production_order_column: effectiveSelectedObjectType,
        resource_column: resourceColumn,
        ...(isDateRange
          ? {
              period_start: effectiveDateRangeStart,
              period_end: effectiveDateRangeEnd,
              granularity: "day",
              selected_date: effectiveDateRangeStart,
            }
          : {
              granularity: effectiveGranularity,
              selected_date: normalizedGranularityDate,
              enabled_granularities: effectiveEnabledGranularities,
            }),
      };

      const requestSignature = JSON.stringify({
        ...payload,
        resources: [...payload.resources].sort(),
        ...(payload.enabled_granularities
          ? { enabled_granularities: [...payload.enabled_granularities].sort() }
          : {}),
      });

      if (inFlightSignatureRef.current === requestSignature) return true;

      const now = Date.now();
      if (
        lastResolvedSignatureRef.current === requestSignature &&
        now - lastResolvedAtRef.current < 1000
      ) {
        return true;
      }

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;

      try {
        inFlightSignatureRef.current = requestSignature;
        const response = await api.post(
          "/kpi/resource-availability",
          payload,
          {},
          "Resource Utilization KPI",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setResult(response);
        lastResolvedSignatureRef.current = requestSignature;
        lastResolvedAtRef.current = Date.now();
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setResult(null);
        setError(err?.message || "Failed to load availability data");
        return false;
      } finally {
        if (inFlightSignatureRef.current === requestSignature) {
          inFlightSignatureRef.current = null;
        }
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      activeFile,
      api,
      breaks,
      configuredResources,
      cycleEndActivity,
      cycleStartActivity,
      enabledGranularities,
      granularity,
      includeNonWorkingDays,
      includeNoDataResources,
      ignoreShortTransitions,
      multiResourceMode,
      resourceColumn,
      selectedDate,
      selectedObjectType,
      shiftEnd,
      shiftStart,
      toleranceThreshold,
      viewResources,
      viewMode,
      dateRangeStart,
      dateRangeEnd,
    ],
  );

  // --- Reset on file change ---
  // Only clear chart output and user selections — NOT metadata state.
  // The metadata sync effect (deps: [metadata, filterState, activeFile]) will
  // re-run after the file switch and repopulate from the new OcelContext value.
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [activeFile]);

  // --- Keep viewResources in sync with configuredResources ---
  useEffect(() => {
    setViewResources((prev) => {
      const allowed = prev.filter((r) => configuredResources.includes(r));
      if (allowed.length > 0) return allowed;
      return configuredResources;
    });
  }, [configuredResources]);

  return {
    // Data
    loading,
    error,
    result,
    datasetLastDate,
    // Metadata
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    enabledGranularities,
    // ViewState
    viewResources,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setCycleStartActivity,
    setCycleEndActivity,
    setConfiguredResources,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setEnabledGranularities,
    // ViewState setters
    setViewResources,
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    // Actions
    addBreak,
    removeBreak,
    updateBreak,
    fetchMetadata,
    fetchAvailability,
  };
};

export { useResourceUtilizationKPI };
